// src/pages/IdentityProvidersPage.tsx
//
// ★★★ WAVE 12 (P2) — ANNEX A STORY 14's CENTRAL CONFIGURATION MODULE, criteria 1
// and 2, on a screen for the first time.
//
// The RTM's bluntest not-built claim was this story's own TITLE: *"NOT BUILT — and
// it is the story's own title: the central configuration MODULE. Criteria 1 and 2
// have no screen and no route: nothing manages WOG/SingPass/CorpPass integration
// settings or user-mapping RULES … A deployment installs EXACTLY ONE identity
// provider, chosen by `PRS2_OIDC_ISSUER`, so 'independently or in combination' is
// not representable."*
//
// ══ ★★★ THE ONE THING THIS SCREEN MUST NOT DO ══
//
// **Show a door as available when it cannot open.** Three of the four seeded
// providers are ENABLED-able and not installable — P2C3 §10.11 and §10.16 need a
// client registration that does not exist for this engagement — so a screen that
// rendered `enabled` as a green tick would be claiming a capability the deployment
// does not have. That is the fabrication `ENGINEERING.md` forbids, and it would be
// believed, because it would look exactly like a working configuration.
//
// So every row states THREE facts, and they are different questions:
//   1. is it OFFERED (`enabled`) — the Authority's policy;
//   2. can this deployment HONOUR it (`installable`) — a fact about the credential;
//   3. what is INSTALLED right now (`installedProvider`) — a fact about the process.
// A reader can see the policy and the running deployment side by side rather than
// inferring one from the other, which is what S-016 is on the register for.
//
// ══ ⚠ AND NO SECRET IS ON THIS PAGE, BECAUSE THERE IS NONE TO SHOW ══
//
// `061_identity_provider_config.sql` keeps no client secret: this catalogue is
// exported to PDF/Excel by the shared report kernel and its history cannot be
// edited in place, so a leaked value could never be removed. The screen says so
// rather than leaving a reader to wonder where the secret is administered.
import { plainCode, plainProse } from '../lib/plain-words'
import { CategoryBadge, CodeBadge } from '../components/StatusBadge'
import { useMemo, useState, type FormEvent } from 'react'
import { SgdsButton, SgdsInput } from '@govtechsg/sgds-web-component/react'
import { api, ApiError } from '../lib/api'
import { useApi } from '../lib/useApi'
import { VersionHistory } from '../components/VersionHistory'
import { EmptyCard, ErrorCard, LoadingCard } from '../components/StateCards'
import { FormResult } from '../components/FormResult'
import { ConfirmWrite } from '../components/ConfirmWrite'
import { BoundedField } from '../components/BoundedField'
import { boundsProblems } from '../lib/published-bounds'
import { targetValue } from '../lib/sgds-dom'
import { formatInstant, formatIsoDate } from '../lib/format'
import { useClientSort, type ClientSortColumns } from '../lib/useClientSort'
import { SortableTh, SortAnnouncement } from '../components/SortableTh'
import type { IdentityMappingRule, IdentityProvider } from '../lib/types'

type FormState = { status: 'idle' } | { status: 'submitting' } | { status: 'success' | 'error'; message: string }

/*
  ★★★ BOTH TABLES ON THIS SCREEN SORT IN THE BROWSER, and the condition that makes
  that honest is checked rather than assumed.

  `GET /api/administration/identity-providers` answers the WHOLE catalogue — the
  four provider keys the domain permits and the mapping rule in force for each. It
  takes no `?limit=`, no `?cursor=` and no `?offset=`, this page has no pager, and
  the route's own bound is the closed provider domain rather than a row count. So
  the browser holds every row there is, and a client sort orders the list rather
  than a window into it.

  ⚠ That is the ONLY circumstance under which `useClientSort` may be used — see
  its own header, and `sortable-header-census.test.ts`, which fails any page that
  sorts in the browser while also paging.
*/
type ProviderSortKey = 'providerKey' | 'displayName' | 'availability' | 'requiredAssuranceLevel' | 'effectiveFrom'

const PROVIDER_COLUMN_LABELS: Readonly<Record<ProviderSortKey, string>> = {
  providerKey: 'Provider',
  displayName: 'Name shown at sign-in',
  availability: 'Availability',
  requiredAssuranceLevel: 'Assurance required',
  effectiveFrom: 'In force from',
}

const PROVIDER_SORT_COLUMNS: ClientSortColumns<IdentityProvider, ProviderSortKey> = {
  providerKey: { type: 'text', valueOf: (p) => p.providerKey },
  displayName: { type: 'text', valueOf: (p) => p.displayName },
  /*
    ⚠ Sorted by the LABEL `availability()` computes, not by `enabled` alone.
    The cell shows one of three states derived from two booleans (enabled, and
    whether the gateway could actually install it), and a reader clicking this
    header is asking to group those three states — not to order one of the two
    inputs to them. Sorting on `enabled` would put a provider that is enabled but
    NOT INSTALLABLE in the same block as one that is working, which is precisely
    the distinction this column exists to draw.
  */
  availability: { type: 'text', valueOf: (p) => availability(p.enabled, p.installable).label },
  /*
    ⚠ `'NONE'` is normalised to null so it joins the null partition rather than
    sorting between two real assurance levels. The cell renders `null` and
    `'NONE'` identically — as "None" — so a reader cannot tell them apart, and
    `useClientSort`'s own rule is that what a reader cannot distinguish must not
    be given two positions.
  */
  requiredAssuranceLevel: {
    type: 'text',
    valueOf: (p) => (p.requiredAssuranceLevel === 'NONE' ? null : p.requiredAssuranceLevel),
  },
  effectiveFrom: { type: 'date', valueOf: (p) => p.effective.from },
}

type RuleSortKey = 'providerKey' | 'claimName' | 'localField' | 'normaliseCase' | 'effectiveFrom'

const RULE_COLUMN_LABELS: Readonly<Record<RuleSortKey, string>> = {
  providerKey: 'Provider',
  claimName: 'Claim read',
  localField: 'Matched against',
  normaliseCase: 'Case-normalised',
  effectiveFrom: 'In force from',
}

const RULE_SORT_COLUMNS: ClientSortColumns<IdentityMappingRule, RuleSortKey> = {
  providerKey: { type: 'text', valueOf: (r) => r.providerKey },
  claimName: { type: 'text', valueOf: (r) => r.claimName },
  localField: { type: 'text', valueOf: (r) => r.localField },
  normaliseCase: { type: 'boolean', valueOf: (r) => r.normaliseCase },
  effectiveFrom: { type: 'date', valueOf: (r) => r.effective.from },
}

/**
 * How a provider's two policy facts read together.
 *
 * ⚠ Four outcomes from two booleans, and each needs its own sentence. The one that
 * matters is `enabled && !installable`: *"the Authority has turned this on and this
 * deployment cannot honour it"* is a real state of the estate, not an error, and it
 * is the state three of the four rows are in.
 */
function availability(enabled: boolean, installable: boolean): { label: string; value: string; tone: string; detail: string } {
  if (enabled && installable) {
    return {
      label: 'Offered',
      value: 'ACTIVE',
      tone: 'text-[var(--sgds-success-color-emphasis)]',
      detail: 'Enabled here, and this deployment holds what it needs to honour it.',
    }
  }
  if (enabled && !installable) {
    return {
      label: 'Offered, but not installable here',
      value: 'ERROR',
      tone: 'text-[var(--sgds-danger-color-emphasis)]',
      detail:
        'Enabled in this catalogue, and this deployment has no credential wired for it, so a sign-in through ' +
        'this door cannot complete. Enabling a federated provider needs a client registration that has to be ' +
        'issued to this deployment; nothing on this screen can supply one.',
    }
  }
  if (!enabled && installable) {
    return {
      label: 'Not offered',
      value: 'INACTIVE',
      tone: 'text-[var(--sgds-body-color-subtle)]',
      detail: 'Turned off here, although this deployment could honour it. Nobody is offered this door.',
    }
  }
  return {
    label: 'Not offered',
    value: 'INACTIVE',
    tone: 'text-[var(--sgds-body-color-subtle)]',
    detail:
      'Turned off here, and this deployment has no credential for it either. Declared so the policy is ' +
      'expressible and auditable before a client registration exists.',
  }
}

export function IdentityProvidersPage() {
  const catalogue = useApi(() => api.identityProviders(), [])

  const [historySubject, setHistorySubject] = useState('')
  const [historyAsRecordedAt, setHistoryAsRecordedAt] = useState('')
  const history = useApi(
    () =>
      historySubject.length === 0
        ? Promise.resolve(null)
        : api.identityProviderSeries(historySubject, historyAsRecordedAt),
    [historySubject, historyAsRecordedAt],
  )

  const [writeKey, setWriteKey] = useState('')
  const [writeName, setWriteName] = useState('')
  const [writeEnabled, setWriteEnabled] = useState(false)
  const [writeFrom, setWriteFrom] = useState('')
  const [writeNote, setWriteNote] = useState('')
  const [form, setForm] = useState<FormState>({ status: 'idle' })

  // ⚠ Named `validation`, matching every sibling screen. `published-bounds.test.ts`
  // greps for the literal `bounds={validation?.<field>}` on purpose — "the exact
  // expression the screens use, so a field that was declared and never wired cannot
  // pass" — so the idiom is the contract here, not a style preference.
  const validation = catalogue.data?.validation

  /**
   * ⚠ The problems come from the PUBLISHED bounds and from one place, so the
   * message a field shows and the message the submit gate reads can never differ —
   * two copies is what `boundsProblems`' own header warns about.
   */
  const problems = boundsProblems(
    validation,
    { displayName: writeName, note: writeNote },
    { displayName: 'Name shown at sign-in', note: 'Note' },
  )

  /*
    ⚠ `useMemo` on both, so the array identity is stable across renders. Without
    it every render hands `useClientSort` a fresh array, its own `useMemo` sees a
    changed dependency and re-sorts — harmless at four rows, and the wrong habit
    to set in a file the next table gets copied out of.
  */
  const providerRows = useMemo(() => catalogue.data?.providers ?? [], [catalogue.data])
  const ruleRows = useMemo(() => catalogue.data?.mappingRules ?? [], [catalogue.data])
  const providerSort = useClientSort(providerRows, PROVIDER_SORT_COLUMNS)
  const ruleSort = useClientSort(ruleRows, RULE_SORT_COLUMNS)
  const providers = providerSort.rows
  const rules = ruleSort.rows

  /**
   * ★★ WAVE 26 — Save FREEZES a sentence and asks; the write happens only from the
   * confirmation (`ConfirmWrite`'s header says why every irreversible write does
   * this now). The sentence is composed here, once, from the fields as they were
   * when Save was pressed — never re-derived from a form the officer may since
   * have edited. `danger`, because closing a door locks every officer who uses it
   * out, and opening one this deployment cannot honour offers a door that will
   * not open.
   */
  const [confirming, setConfirming] = useState<string | null>(null)

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    // The same gate the button reads — a refusal the screen already predicted
    // must not become a sentence asking the officer to confirm it.
    if (problems.any) return
    const key = writeKey.trim().toUpperCase()
    const name = writeName.trim()
    setConfirming(
      writeEnabled
        ? `Open the ${plainCode(key)} sign-in door from ${writeFrom}: shown as "${name}" and offered at sign-in. If this deployment holds no credential for ${plainCode(key)}, the door is shown as not installable rather than made to work. The window now in force is closed, not edited. There is no undo. A mistake is corrected by another version.`
        : `Close the ${plainCode(key)} sign-in door from ${writeFrom}: shown as "${name}" and not offered. From that day every officer who signs in through ${plainCode(key)} is locked out until another version opens it again. The window now in force is closed, not edited. There is no undo. A mistake is corrected by another version.`,
    )
  }

  const performWrite = async () => {
    setConfirming(null)
    setForm({ status: 'submitting' })
    try {
      await api.supersedeIdentityProvider(writeKey.trim().toUpperCase(), {
        effectiveFrom: writeFrom,
        ...(writeNote.trim().length > 0 && { note: writeNote.trim() }),
        values: { displayName: writeName.trim(), enabled: writeEnabled },
      })
      setForm({
        status: 'success',
        message: `${writeKey.trim().toUpperCase()} takes effect from ${writeFrom}. The previous window is closed, not edited.`,
      })
      setWriteKey('')
      setWriteName('')
      setWriteEnabled(false)
      setWriteFrom('')
      setWriteNote('')
      catalogue.reload()
      if (historySubject.length > 0) history.reload()
    } catch (error) {
      setForm({
        status: 'error',
        message: error instanceof ApiError ? error.message : 'Could not change this provider.',
      })
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="text-3xl mb-2">Identity providers</h1>
        <p className="text-[var(--sgds-body-color-subtle)] max-w-[75ch]">
          Which sign-in doors this deployment offers, and how a verified token claim resolves to a local account.
          Effective-dated: a provider is never edited in place: a change closes the window in force and opens a new
          one, so what was offered on any past date stays answerable.
        </p>
      </header>

      {/*
        ⚠ SAID ONCE, AT THE TOP, because a reader's first question about a page like
        this is "where is the client secret?" — and the answer is a design decision
        with three reasons behind it, not an omission.
      */}
      <div className="pt-well p-4">
        <p className="font-semibold">No client secret is held or shown here</p>
        <p className="mt-2 text-sm text-[var(--sgds-body-color-subtle)] max-w-[75ch]">
          Secrets stay in the deployment&rsquo;s own environment. This catalogue is exported to PDF and Excel by the
          same report kernel every other catalogue uses, and its history cannot be edited in place, so a secret
          stored here would be a secret in a downloadable spreadsheet that could never afterwards be removed.
        </p>
      </div>

      {catalogue.error !== null ? (
        <ErrorCard title="Could not load the identity providers" message={catalogue.error} onRetry={catalogue.reload} />
      ) : catalogue.initialLoading ? (
        <LoadingCard label="Loading identity providers" rows={4} />
      ) : providers.length === 0 ? (
        <EmptyCard
          title="No identity providers are configured"
          message="No sign-in provider is in force on the date shown. Every window has been closed; open a new one to offer a provider."
        />
      ) : (
        <section className="pt-card">
          <h2 className="text-lg mb-1">Doors this deployment declares</h2>
          <p className="text-sm text-[var(--sgds-color-subtle)] mb-4 max-w-[75ch]">
            Currently running: <strong>{plainCode(catalogue.data?.installedProvider ?? '')}</strong>. That is what
            the service actually installed when it started: shown beside the policy below so the two can be read together
            rather than one inferred from the other.
          </p>
          {/*
            ⚠ `busy={false}`, and that is correct rather than lazy. `sortAnnouncement`
            takes `busy` because a SERVER sort leaves the old order on screen between
            the click and the response, and announcing then would state a change that
            has not happened. This sort is synchronous: by the time this renders the
            rows ARE in the new order, so there is no in-flight window to stay silent
            through.
          */}
          <SortAnnouncement
            sort={providerSort.sort}
            interacted={providerSort.interacted}
            busy={false}
            columnLabels={PROVIDER_COLUMN_LABELS}
            rowCount={providers.length}
          />
          <div className="pt-table-wrap">
            <table className="pt-table">
              <caption className="sr-only">Identity providers currently in force, and whether each can be honoured</caption>
              <thead>
                <tr>
                  <SortableTh label={PROVIDER_COLUMN_LABELS.providerKey} sortKey="providerKey" sort={providerSort.sort} onSort={providerSort.onSort} />
                  <SortableTh label={PROVIDER_COLUMN_LABELS.displayName} sortKey="displayName" sort={providerSort.sort} onSort={providerSort.onSort} />
                  <SortableTh label={PROVIDER_COLUMN_LABELS.availability} sortKey="availability" sort={providerSort.sort} onSort={providerSort.onSort} />
                  <SortableTh label={PROVIDER_COLUMN_LABELS.requiredAssuranceLevel} sortKey="requiredAssuranceLevel" sort={providerSort.sort} onSort={providerSort.onSort} />
                  <SortableTh label={PROVIDER_COLUMN_LABELS.effectiveFrom} sortKey="effectiveFrom" sort={providerSort.sort} onSort={providerSort.onSort} />
                  <th scope="col" className="pt-no-print" data-unsortable="one control per row, not data — a sortable header over buttons would claim to order something with no order">
                    History
                  </th>
                </tr>
              </thead>
              <tbody>
                {providers.map((provider) => {
                  const state = availability(provider.enabled, provider.installable)
                  return (
                    <tr key={provider.id}>
                      <td><CodeBadge value={provider.providerKey} /></td>
                      <td>{provider.displayName}</td>
                      <td>
                        <CategoryBadge kind="state" value={state.value} label={state.label} />
                        <span className="block text-xs text-[var(--sgds-body-color-subtle)] max-w-[46ch] mt-1">
                          {plainProse(state.detail)}
                        </span>
                      </td>
                      <td className="text-sm">
                        {/* ⚠ HELD AND NOT YET ENFORCED, and the screen says which. No verified
                            token in this estate carries an assurance claim, because no
                            SingPass/CorpPass registration exists to issue one. A blank cell
                            would read as "no requirement"; the sentence says the policy is
                            recorded and nothing checks it yet. */}
                        {provider.requiredAssuranceLevel === null || provider.requiredAssuranceLevel === 'NONE' ? (
                          <span className="text-[var(--sgds-body-color-subtle)]">None</span>
                        ) : (
                          <>
                            <span>{plainCode(provider.requiredAssuranceLevel)}</span>
                            <span className="block text-xs text-[var(--sgds-body-color-subtle)] mt-1">
                              Recorded as policy. Nothing checks it yet: no token in this estate carries an
                              assurance claim.
                            </span>
                          </>
                        )}
                      </td>
                      <td className="pt-tabular" title={formatInstant(provider.recordedAt)}>
                        {formatIsoDate(provider.effective.from)}
                      </td>
                      <td className="pt-no-print">
                        <SgdsButton
                          variant={historySubject === provider.providerKey ? 'primary' : 'ghost'}
                          size="sm"
                          type="button"
                          onClick={() => {
                            // ⚠ Changing provider RESETS the rewind — an instant chosen while
                            // reading one provider is not a statement about another.
                            setHistoryAsRecordedAt('')
                            setHistorySubject(historySubject === provider.providerKey ? '' : provider.providerKey)
                          }}
                        >
                          {historySubject === provider.providerKey ? 'Hide history' : 'History'}
                        </SgdsButton>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          {(catalogue.data?.notes ?? []).length > 0 && (
            <ul className="mt-4 flex flex-col gap-2 text-xs text-[var(--sgds-color-subtle)] m-0 p-0 list-none max-w-[80ch]">
              {(catalogue.data?.notes ?? []).map((note) => (
                <li key={note}>{plainProse(note)}</li>
              ))}
            </ul>
          )}
        </section>
      )}

      {historySubject.length > 0 && (
        <VersionHistory
          subject={historySubject}
          entries={history.data?.entries ?? []}
          loading={history.loading}
          error={history.error}
          onRetry={history.reload}
          asRecordedAt={historyAsRecordedAt}
          onAsRecordedAtChange={setHistoryAsRecordedAt}
          columns={[
            { header: 'Name', cell: (entry) => entry.displayName },
            { header: 'Offered', cell: (entry) => (entry.enabled ? 'yes' : 'no') },
          ]}
        />
      )}

      {/* ─────────────────────── criterion 2: the mapping rules ─────────────────────── */}
      <section className="pt-card">
        <h2 className="text-lg mb-1">How a claim becomes an account</h2>
        <p className="text-sm text-[var(--sgds-color-subtle)] mb-4 max-w-[75ch]">
          Until this catalogue existed the rule was compiled in: the verified{' '}
          <span className="pt-mono">email</span> claim matched against an account&rsquo;s email, lower-cased: a
          reasonable default that nobody could see, audit or change. It is written down now.
        </p>
        {rules.length === 0 ? (
          <div className="pt-well p-4">
            <p className="font-semibold">No mapping rule is recorded</p>
            <p className="mt-2 text-sm text-[var(--sgds-body-color-subtle)]">
              {/* ⚠ Absence has a MEANING here and it is not "broken": the sign-in path
                  falls back to the compiled-in rule, which is what ran before this
                  catalogue existed. Saying so is the difference between "no data" and
                  "not built". */}
              Federated sign-in falls back to the built-in rule: the <span className="pt-mono">email</span> claim,
              lower-cased, matched against the account&rsquo;s email. That is the behaviour that ran before this
              catalogue existed, so an empty list changes nothing; it only means the rule is not written down.
            </p>
          </div>
        ) : (
          <>
          <SortAnnouncement
            sort={ruleSort.sort}
            interacted={ruleSort.interacted}
            busy={false}
            columnLabels={RULE_COLUMN_LABELS}
            rowCount={rules.length}
          />
          <div className="pt-table-wrap">
            <table className="pt-table">
              <caption className="sr-only">The claim-to-account mapping rule in force for each provider</caption>
              <thead>
                <tr>
                  <SortableTh label={RULE_COLUMN_LABELS.providerKey} sortKey="providerKey" sort={ruleSort.sort} onSort={ruleSort.onSort} />
                  <SortableTh label={RULE_COLUMN_LABELS.claimName} sortKey="claimName" sort={ruleSort.sort} onSort={ruleSort.onSort} />
                  <SortableTh label={RULE_COLUMN_LABELS.localField} sortKey="localField" sort={ruleSort.sort} onSort={ruleSort.onSort} />
                  <SortableTh label={RULE_COLUMN_LABELS.normaliseCase} sortKey="normaliseCase" sort={ruleSort.sort} onSort={ruleSort.onSort} />
                  <SortableTh label={RULE_COLUMN_LABELS.effectiveFrom} sortKey="effectiveFrom" sort={ruleSort.sort} onSort={ruleSort.onSort} />
                </tr>
              </thead>
              <tbody>
                {rules.map((rule) => (
                  <tr key={rule.id}>
                    <td><CodeBadge value={rule.providerKey} /></td>
                    <td className="pt-mono">{rule.claimName}</td>
                    <td className="pt-mono">{rule.localField}</td>
                    <td><CategoryBadge kind="grant" value={rule.normaliseCase ? 'YES' : 'NO'} label={rule.normaliseCase ? 'Yes' : 'No'} /></td>
                    <td className="pt-tabular">{formatIsoDate(rule.effective.from)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </section>

      {/* ─────────────────────── the write ─────────────────────── */}
      <section className="pt-card flex flex-col gap-5 pt-no-print">
        <div>
          <h2 className="text-xl mb-1">Change a provider</h2>
          <p className="text-sm text-[var(--sgds-body-color-subtle)] max-w-[75ch]">
            Superseding is the only way a provider changes: there is no edit in place. The window in force is closed
            and a new one opens from the date given, so *&ldquo;what was offered last March&rdquo;* stays answerable.
            The date must be AFTER the current window starts, or the two would overlap and *&ldquo;which
            configuration applies&rdquo;* would have two answers.
          </p>
        </div>
        <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
          <SgdsInput placeholder=""
            label="Provider key"
            hintText="One of the providers listed below. Anything else is refused."
            value={writeKey}
            onSgdsInput={(e) => setWriteKey(targetValue(e))}
          />
          {/*
            ★★ BOUND FROM THE PAYLOAD, never from a number typed here. `displayName`
            has a real database CHECK (1..120), and P2C11 §14.6 licenses this screen to
            SHOW the rule and never to own it — a client copy is how one bound becomes
            two different numbers.
          */}
          <BoundedField
            label="Name shown at sign-in"
            example="What an officer reads on the sign-in screen."
            value={writeName}
            onValueChange={setWriteName}
            bounds={validation?.displayName}
            problem={problems.of.displayName}
            required
          />
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-1"
              checked={writeEnabled}
              onChange={(event) => setWriteEnabled(event.target.checked)}
            />
            <span>
              <span className="font-semibold">Offer this door</span>
              <span className="block text-xs text-[var(--sgds-body-color-subtle)] max-w-[62ch] mt-1">
                ⚠ Offering a federated provider this deployment has no credential for will show it as{' '}
                <strong>not installable</strong> above rather than making it work. The catalogue records policy; it
                cannot supply a client registration.
              </span>
            </span>
          </label>
          <SgdsInput placeholder=""
            label="In force from"
            hintText="YYYY-MM-DD, and it must be after the date the current window started."
            value={writeFrom}
            onSgdsInput={(e) => setWriteFrom(targetValue(e))}
          />
          <BoundedField
            label="Note"
            example="Why this changed. Kept on the permanent record beside the change."
            value={writeNote}
            onValueChange={setWriteNote}
            bounds={validation?.note}
            problem={problems.of.note}
            multiline
          />
          <div>
            {/* ⚠ Gated on the SAME problems the fields render, so a refusal the screen
                already predicted never reaches the route. */}
            <SgdsButton
              type="submit"
              variant="primary"
              disabled={form.status === 'submitting' || problems.any || confirming !== null}
            >
              Supersede provider
            </SgdsButton>
          </div>
          {confirming !== null && (
            <ConfirmWrite
              summary={confirming}
              confirmLabel="Yes, apply it"
              variant="danger"
              onConfirm={() => void performWrite()}
              onCancel={() => setConfirming(null)}
            />
          )}
          <FormResult state={form} />
        </form>
      </section>
    </div>
  )
}
